import { useMemo } from "react";
import { Plus, Sparkles } from "lucide-react";
import { useMenu } from "../context/MenuContext";
import { useCart } from "../context/CartContext";
import { itemTakesSpice, DEFAULT_SPICE } from "./VariantPicker";
import { toast } from "sonner";

// Slug → recommendation copy. We pick ONE item from each slot the cart is missing.
const SUGGESTION_SLOTS = [
  { slug: "tandoori-breads", title: "Add a fresh-pulled naan", emoji: "🫓" },
  { slug: "beverages", title: "Wash it down", emoji: "🥤" },
  { slug: "desserts", title: "Sweet finish?", emoji: "🍮" },
  { slug: "appetizers", title: "Start with a snack", emoji: "🥟" },
];

export default function CompleteYourMeal() {
  const { categories } = useMenu();
  const { items, add } = useCart();

  const suggestions = useMemo(() => {
    const cartCategorySlugs = new Set();
    items.forEach((ci) => {
      // We can't know category just from cart item (we stored only id+variant);
      // so look it up from the menu.
      categories.forEach((c) => {
        if (c.items.some((mi) => mi.id === ci.item_id)) {
          cartCategorySlugs.add(c.slug);
        }
      });
    });

    const out = [];
    for (const slot of SUGGESTION_SLOTS) {
      if (cartCategorySlugs.has(slot.slug)) continue;
      const cat = categories.find((c) => c.slug === slot.slug);
      if (!cat) continue;
      // Pick the first orderable item with a price (or first variant)
      const pick = cat.items.find(
        (i) => (i.price != null || i.variants?.length) && i.price !== null
      ) || cat.items.find((i) => i.variants?.length);
      if (!pick) continue;
      const price = pick.price != null ? pick.price : pick.variants?.[0]?.price;
      if (price == null) continue;
      out.push({ ...slot, item: pick, price });
      if (out.length >= 3) break;
    }
    return out;
  }, [categories, items]);

  if (suggestions.length === 0) return null;

  const quickAdd = (s) => {
    const variant = s.item.variants?.[0] || null;
    add({
      item_id: s.item.id,
      name: s.item.name,
      variant_name: variant?.name || null,
      variant_label: variant?.name || null,
      price: variant ? variant.price : s.item.price,
      // Default spice if applicable; user can edit at item-level later
      spice_level: itemTakesSpice(s.item) ? DEFAULT_SPICE : null,
    });
    toast.success(`Added ${s.item.name} to cart`);
  };

  return (
    <section data-testid="complete-your-meal" className="rounded-3xl p-6 glass glass-shine">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9853E] font-bold flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> Complete your meal
      </div>
      <h3 className="font-display text-xl font-extrabold text-[#0E0E1A] mt-1">
        Round it out — these go great with your order.
      </h3>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="suggestions-grid">
        {suggestions.map((s) => (
          <button
            key={s.slug}
            type="button"
            onClick={() => quickAdd(s)}
            data-testid={`suggest-${s.slug}`}
            className="group text-left rounded-2xl p-4 border border-white/40 bg-white/40 hover:bg-white/70 hover:border-[#C9853E] transition-all"
          >
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#57534E] font-bold flex items-center gap-1.5">
              <span aria-hidden>{s.emoji}</span> {s.title}
            </div>
            <div className="font-display text-base font-bold text-[#0E0E1A] mt-1 leading-tight">
              {s.item.name}
            </div>
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-sm font-bold text-[#C9853E]">
                ${s.price.toFixed(2)}
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-[#0E0E1A] group-hover:text-[#C9853E]">
                <Plus className="w-3 h-3" /> Add
              </span>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
